import { useEffect } from 'react'
import { SOCKET } from '../../constants/socket'
import { useAppSelector } from '../../redux/hooks'
import { socket } from '../../services/socket-setup'

/** Only a visible tab counts as the app being open (and its user as online). */
export const currentAppState = () =>
  typeof document !== 'undefined' && document.visibilityState === 'visible' ? 'foreground' : 'background'

/**
 * Reports what this client is currently showing so the server can decide whether
 * a new message needs an FCM push.
 *
 * The server suppresses a push only when the recipient is in the FOREGROUND with
 * that exact conversation open (Case 4) — every other case still pushes. So the
 * two fields that matter are `appState` and `conversationId`; `screen` is sent
 * for completeness/debugging.
 *
 * `conversationId` intentionally uses `selectedChat.id`, which matches what the
 * server checks: the group id for a group, and the OTHER user's id for a direct
 * chat (the same `chat_id` convention used throughout the chat list).
 *
 * Re-reports on: mount, socket (re)connect, tab visibility change, and whenever
 * the open conversation changes — including closing one (conversationId: null).
 */
const usePresenceReporter = () => {
  const { selectedChat } = useAppSelector((state) => state.chat)
  const conversationId = selectedChat?.id ? String(selectedChat.id) : null

  useEffect(() => {
    const report = () => {
      if (!socket.connected) return

      socket.emit(SOCKET.Emitters.Presence_Update, {
        appState: currentAppState(),
        screen: conversationId ? 'chat' : 'chat-list',
        conversationId,
      })
    }

    report()

    // Re-report after a reconnect: the server drops presence with the socket.
    socket.on('connect', report)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', report)
    }

    return () => {
      socket.off('connect', report)
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', report)
      }
    }
  }, [conversationId])
}

export default usePresenceReporter
